/**
 * PartyCombo — sélecteur de client / fournisseur avec saisie libre : on tape le numéro OU le nom, la liste se filtre (ordre = numéro).
 * Clavier : ↓ ↑ pour surligner, Entrée pour choisir, Échap pour fermer. Valeur vide = « tous » (filtres) ou « aucun » (formulaires).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { inputCls } from "../comptabilite/_shared";

const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function PartyCombo({ parties, value, onChange, placeholder, testId, disabled, className = inputCls }) {
  const list = useMemo(() => (parties || []).filter((p) => p.is_active !== false || p.id === value), [parties, value]);
  const selected = list.find((p) => p.id === value);
  const labelOf = (p) => (p ? (p.number ? `${p.number} — ${p.name}` : p.name) : "");
  const [text, setText] = useState(labelOf(selected));
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrap = useRef(null);
  const listRef = useRef(null);
  const selLabel = labelOf(selected);
  useEffect(() => { setText(selLabel); }, [selLabel]);

  const q = norm(text);
  const typing = open && q !== norm(selLabel);
  const matches = useMemo(() => {
    if (!typing || !q) return list;
    return list.filter((p) => norm(p.number).startsWith(q) || norm(p.name).includes(q) || norm(labelOf(p)).includes(q));
  }, [list, q, typing]);
  const shown = matches.slice(0, 60);

  useEffect(() => { setHi(0); }, [q, open]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) close(); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  });
  useEffect(() => { listRef.current?.children?.[hi]?.scrollIntoView?.({ block: "nearest" }); }, [hi]);

  const close = () => { setOpen(false); setText(selLabel); };
  const pick = (p) => { onChange(p ? p.id : ""); setText(labelOf(p)); setOpen(false); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); if (!open) setOpen(true); else setHi((h) => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { if (open && shown[hi]) { e.preventDefault(); pick(shown[hi]); } }
    else if (e.key === "Escape") { close(); }
    else if (e.key === "Tab") { if (open && typing && shown.length === 1) pick(shown[0]); else setOpen(false); }
  };

  return (
    <div ref={wrap} className="relative" data-testid={testId ? `${testId}-combo` : undefined}>
      <input value={text} disabled={disabled} placeholder={placeholder || "N° ou nom…"} autoComplete="off"
        onFocus={(e) => { setOpen(true); e.target.select(); }} onChange={(e) => { setText(e.target.value); setOpen(true); }} onKeyDown={onKey}
        className={`${className} pr-14`} data-testid={testId} role="combobox" aria-expanded={open} aria-autocomplete="list" />
      <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
        {value && !disabled && <button type="button" tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(null)} className="p-1 rounded text-slate-400 hover:text-rose-600" title="Effacer" data-testid={testId ? `${testId}-clear` : undefined}><X className="w-3.5 h-3.5" /></button>}
        <button type="button" tabIndex={-1} disabled={disabled} onMouseDown={(e) => e.preventDefault()} onClick={() => (open ? close() : setOpen(true))} className="p-1 rounded text-slate-400 hover:text-slate-700 disabled:opacity-40" data-testid={testId ? `${testId}-toggle` : undefined}><ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      </div>
      {open && !disabled && (
        <ul ref={listRef} role="listbox" className="absolute z-30 mt-1 left-0 right-0 max-h-64 overflow-auto bg-white border border-slate-200 rounded-xl shadow-lg py-1 text-sm" data-testid={testId ? `${testId}-list` : undefined}>
          {!shown.length && <li className="px-3 py-2 text-xs text-slate-400">Aucun résultat pour « {text} »</li>}
          {shown.map((p, i) => (
            <li key={p.id} role="option" aria-selected={p.id === value} onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setHi(i)} onClick={() => pick(p)}
              className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${i === hi ? "bg-emerald-50" : ""} ${p.id === value ? "font-semibold text-emerald-800" : "text-slate-800"}`} data-testid={testId ? `${testId}-option-${p.id}` : undefined}>
              {p.number && <span className="font-mono text-xs text-teal-700 w-12 shrink-0">{p.number}</span>}
              <span className="truncate">{p.name}</span>
              {p.is_active === false && <span className="ml-auto text-[10px] uppercase text-slate-400">inactif</span>}
            </li>
          ))}
          {matches.length > shown.length && <li className="px-3 py-1.5 text-[11px] text-slate-400">… {matches.length - shown.length} autre(s) — précisez la recherche</li>}
        </ul>
      )}
    </div>
  );
}
